/**
 * AlertDetail Page - Shows detailed information about an alert
 * Including related signals, sources, and AI-generated analysis
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ExternalLink,
  Calendar,
  Building2,
  TrendingUp,
  FileText,
  AlertCircle,
  Clock,
  Tag,
  Briefcase,
  ChevronRight
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { ALERT_TYPE_CONFIG, PRIORITY_LABELS } from '../../services/alertService';

export default function AlertDetail() {
  const { alertId } = useParams();
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (alertId) {
      fetchAlertDetail();
    }
  }, [alertId]);

  const fetchAlertDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch the alert
      const { data: alertData, error: alertError } = await supabase
        .from('alerts')
        .select(`
          *,
          buyers:buyer_id(id, company_name, stock_ticker, headquarters, therapeutic_areas, tech_platforms)
        `)
        .eq('id', alertId)
        .single();

      if (alertError) throw alertError;
      setAlert(alertData);

      // Mark as read if not already
      if (!alertData.is_read) {
        await supabase
          .from('alerts')
          .update({ is_read: true, read_at: new Date().toISOString() })
          .eq('id', alertId);
      }

      // Fetch related signals for this buyer
      if (alertData.buyer_id) {
        const { data: signalData, error: signalError } = await supabase
          .from('buyer_signals')
          .select('*')
          .eq('buyer_id', alertData.buyer_id)
          .order('signal_date', { ascending: false })
          .limit(10);

        if (!signalError && signalData) {
          setSignals(signalData);
        }
      }
    } catch (err) {
      console.error('Error fetching alert detail:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error || !alert) {
    return (
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-red-800">Alert Not Found</h2>
          <p className="text-red-600 mt-1">{error || 'The alert you are looking for does not exist.'}</p>
        </div>
      </div>
    );
  }

  const typeConfig = ALERT_TYPE_CONFIG[alert.alert_type] || { icon: 'bell', color: 'gray', label: alert.alert_type };
  const priorityLabel = PRIORITY_LABELS[alert.priority] || 'medium';
  const buyer = alert.buyers;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Notifications
      </button>

      {/* Alert Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-lg bg-${typeConfig.color}-100 flex items-center justify-center flex-shrink-0`}>
            <TrendingUp className={`w-6 h-6 text-${typeConfig.color}-600`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${typeConfig.color}-100 text-${typeConfig.color}-700`}>
                {typeConfig.label}
              </span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                priorityLabel === 'critical' ? 'bg-red-100 text-red-700' :
                priorityLabel === 'high' ? 'bg-orange-100 text-orange-700' :
                priorityLabel === 'medium' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {priorityLabel.charAt(0).toUpperCase() + priorityLabel.slice(1)} Priority
              </span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{alert.title}</h1>
            <p className="text-gray-600">{alert.description}</p>
            <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {new Date(alert.created_at).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Buyer Information */}
      {buyer && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Related Company
          </h2>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-8 h-8 text-gray-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-gray-900">
                {buyer.company_name}
                {buyer.stock_ticker && (
                  <span className="ml-2 text-sm font-normal text-gray-500">({buyer.stock_ticker})</span>
                )}
              </h3>
              {buyer.headquarters && (
                <p className="text-gray-600">{buyer.headquarters}</p>
              )}
              {buyer.therapeutic_areas && buyer.therapeutic_areas.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {buyer.therapeutic_areas.slice(0, 5).map((area, i) => (
                    <span key={i} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                      {area}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Summary */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Analysis Summary
        </h2>
        <div className="prose prose-sm max-w-none">
          {alert.data?.analysis ? (
            <div className="text-gray-700 whitespace-pre-wrap">{alert.data.analysis}</div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-600 mb-4">
                <strong>Key Insights:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>This alert was triggered based on {typeConfig.label.toLowerCase()} activity</li>
                {buyer && <li>Company: {buyer.company_name} has shown significant activity in relevant areas</li>}
                <li>Priority level: {priorityLabel} - {
                  priorityLabel === 'critical' ? 'Immediate attention recommended' :
                  priorityLabel === 'high' ? 'Review within 24 hours' :
                  priorityLabel === 'medium' ? 'Review when convenient' :
                  'For your information'
                }</li>
              </ul>
              <p className="text-gray-500 text-sm mt-4 italic">
                Detailed AI analysis will be available when more signal data is collected.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Related Signals / Sources */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Briefcase className="w-5 h-5" />
          Related Sources & Signals
        </h2>

        {signals.length > 0 ? (
          <div className="space-y-4">
            {signals.map((signal) => (
              <div
                key={signal.id}
                className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                        {signal.signal_type?.replace(/_/g, ' ').toUpperCase()}
                      </span>
                      <span className="text-sm text-gray-500">
                        {signal.source_name || 'Unknown Source'}
                      </span>
                    </div>

                    {signal.content_snippet && (
                      <p className="text-gray-700 text-sm mb-2 line-clamp-3">
                        {signal.content_snippet}
                      </p>
                    )}

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(signal.signal_date).toLocaleDateString()}
                      </span>
                      {signal.strength && (
                        <span className="flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" />
                          Strength: {signal.strength}/10
                        </span>
                      )}
                    </div>

                    {signal.keywords && signal.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {signal.keywords.slice(0, 5).map((keyword, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                            {keyword}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {signal.source_url && (
                    <a
                      href={signal.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      title="View Source"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No related signals found for this alert</p>
            <p className="text-sm text-gray-400 mt-1">
              Signals are collected from SEC filings, conference presentations, and other sources
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Back
        </button>
        {buyer && (
          <Link
            to={`/seller/matching?buyer=${buyer.id}`}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            View Match Details
            <ChevronRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
}
